import {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import './occurrences.daily-care-log.screen.scss';
import {EntityOccurrencesRepository} from '../../../../repositories/entity-occurrences';
import {Fab, Stack, Typography} from '@mui/material';
import {translations} from '../../../../i18n/translations';
import {Add} from '@mui/icons-material';
import {ScreenPaths} from '../../../../enums';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {useAuth} from '../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../backend/enums/role-permission-type';
import OccurrencesListDailyCareLogScreen from './occurrences-list';
import {SkeletonsList} from '../home/home.daily-care-log.screen';
import {TransformedOccurrence, transformOccurrences} from '../../../../utils/data/occurrences';
import {useDailyCareLogFilters} from '../../../../contexts/dailycare-log-filters/context';
import {DailyCareLogFiltersDrawer} from '../../../../components/daily-care-log/filters-drawer';
import moment from 'moment';
import {ContentPage} from '../../../../components/content-page/content-page';
import DateCarousel from '../../../../components/daily-care-log/date-carousel/date-carousel';
import {useEntity} from '../../../../contexts/entity/context';
import eventEmitter from '../../../../events/event-emitter';
import {PushNotificationReceived, PUSH_NOTIFICATION_RECEIVED} from '../../../../events/event-types';
import {NotificationType} from '../../../../backend/enums/notification-type';

type OccurrencesDailyCareLogScreenProps = {
  seniors?: AffiliatedUser[];
  professionals?: AffiliatedUser[];
};

export default function OccurrencesDailyCareLogScreen({
  seniors,
  professionals,
}: OccurrencesDailyCareLogScreenProps) {
  const {entityId}: {entityId: string} = useParams();
  const {t} = useTranslation();
  let history = useHistory();
  const {tasksFilters} = useDailyCareLogFilters();
  const dailyCareLogContext = useDailyCareLog();
  const {hasWritePermission} = useAuth();
  const {
    updateEntityOccurrencesCount,
    updateEntityOccurrencesTypes,
    state: {occurrencesTypes},
  } = useEntity();
  const location = useLocation();
  const [occurrences, setOccurrences] = useState<TransformedOccurrence[]>();
  const [filteredOccurrences, setFilteredOccurrences] = useState<TransformedOccurrence[]>();
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState<boolean>(false);
  const searchParams = new URLSearchParams(location.search);
  const date = searchParams.get('date');
  const selectedDay = useMemo(() => {
    if (!date) return moment();
    const d = moment(date);
    return d.isValid() ? d : moment();
  }, [date]);

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(false);
    dailyCareLogContext.toggleBottomNavigation(true, true);

    const updateOccurrences = async () => {
      await updateEntityOccurrencesCount();

      // Avoid refetching types when they are already in context. Refetching them here changes
      // `occurrencesTypes` and triggers a second occurrences-list GET on tab navigation.
      if (!occurrencesTypes) {
        await updateEntityOccurrencesTypes();
      }
    };

    updateOccurrences();

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshOccurrences = useCallback(async () => {
    if (!entityId || !occurrencesTypes) return;

    try {
      const data = await EntityOccurrencesRepository.instance.getOccurrences(
        entityId,
        selectedDay.clone().startOf('day').toISOString(),
        selectedDay.clone().endOf('day').toISOString()
      );

      const occurrences = await transformOccurrences(
        data,
        occurrencesTypes,
        seniors,
        professionals
      );
      setOccurrences(occurrences);
    } catch (e) {
      console.error(e);
      setOccurrences([]);
    }
  }, [entityId, occurrencesTypes, professionals, selectedDay, seniors]);

  useEffect(() => {
    refreshOccurrences();
  }, [refreshOccurrences]);

  useEffect(() => {
    const onPushNotificationReceived = (notification: PushNotificationReceived) => {
      if (
        notification.notificationType !== NotificationType.NewOccurrence &&
        notification.notificationType !== NotificationType.NewOccurrenceAssigned
      ) {
        return;
      }

      if (notification.entityId && notification.entityId !== entityId) {
        return;
      }

      setOccurrences(undefined);
      refreshOccurrences();
    };

    eventEmitter.on(PUSH_NOTIFICATION_RECEIVED, onPushNotificationReceived);

    return () => {
      eventEmitter.off(PUSH_NOTIFICATION_RECEIVED, onPushNotificationReceived);
    };
  }, [entityId, refreshOccurrences]);

  useEffect(() => {
    if (!filtersDrawerOpen) {
      let filtered: TransformedOccurrence[] = [];
      if (
        tasksFilters.status.length === 0 &&
        tasksFilters.occurrencesTypesGeneral.length === 0 &&
        tasksFilters.occurrencesTypesPatients.length === 0 &&
        tasksFilters.users.length === 0 &&
        tasksFilters.socialResponseIds.length === 0
      ) {
        setFilteredOccurrences(occurrences);
        return;
      }

      filtered =
        occurrences?.filter((occurrence) => {
          const statusMatch =
            tasksFilters.status.length === 0 || tasksFilters.status.includes(occurrence.status);
          const typeGeneralMatch =
            tasksFilters.occurrencesTypesGeneral.length === 0 ||
            tasksFilters.occurrencesTypesGeneral.includes(occurrence.typeId);
          const typePatientsMatch =
            tasksFilters.occurrencesTypesPatients.length === 0 ||
            tasksFilters.occurrencesTypesPatients.includes(occurrence.typeId);
          const userMatch =
            tasksFilters.users.length === 0 ||
            (occurrence.patient && tasksFilters.users.includes(occurrence.patient.id));
          const socialResponseMatch =
            tasksFilters.socialResponseIds.length === 0 ||
            (occurrence.patient &&
              occurrence.patient.socialResponses?.some((sr) =>
                tasksFilters.socialResponseIds.includes(sr.id)
              ));

          return (
            statusMatch && typeGeneralMatch && typePatientsMatch && userMatch && socialResponseMatch
          );
        }) || [];

      setFilteredOccurrences(filtered);
    }
  }, [filtersDrawerOpen, occurrences, tasksFilters]);

  return (
    <ContentPage>
      <DateCarousel
        selectedDate={selectedDay}
        onDateChanged={(value) => {
          searchParams.set('date', value.format('YYYY-MM-DD'));
          history.push({search: searchParams.toString()});
        }}
      />
      <Stack className="occurrences-container" justifyContent="center" direction="row">
        <DailyCareLogFiltersDrawer
          type="occurrences"
          open={filtersDrawerOpen}
          activeFilters={tasksFilters}
          onDrawerClose={() => setFiltersDrawerOpen(false)}
          onFiltersSave={() => {}}
        />
        {!occurrencesTypes || !occurrences || !filteredOccurrences ? (
          <SkeletonsList count={6} />
        ) : (
          <OccurrencesListDailyCareLogScreen
            entityId={entityId}
            occurrences={filteredOccurrences || occurrences}
            selectedDay={selectedDay}
            onFiltersOpen={() => setFiltersDrawerOpen(true)}
          />
        )}
        {hasWritePermission(RolePermissionType.DailyCareLog_Occurrences) && (
          <Fab
            color="primary"
            className="occurrences-floating"
            size="large"
            variant={occurrences?.length === 0 ? 'extended' : 'circular'}
            onClick={() =>
              history.push(
                `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_NewOccurrence}?date=${selectedDay.format('YYYY-MM-DD')}`
              )
            }
          >
            <Add />
            {occurrences?.length === 0 && (
              <Typography>{t(translations.daily_care_log.occurrences.new_occurrence)}</Typography>
            )}
          </Fab>
        )}
      </Stack>
    </ContentPage>
  );
}
